import React, { useState } from 'react';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';

import { API_BASE_URL as API_URL } from '../utils/apiConfig';

/**
 * Utility function to convert JSON data to CSV string
 */
export const jsonToCSV = (data, columns) => {
  if (!data || !data.length) return '';
  
  // Use provided columns or extract from first row
  const headers = columns || Object.keys(data[0]);
  
  // Create CSV header
  const csvHeader = headers.join(',');
  
  // Create CSV rows
  const csvRows = data.map(row => {
    return headers.map(header => {
      let cell = row[header];
      // Handle null/undefined
      if (cell === null || cell === undefined) cell = '';
      // Handle numbers
      if (typeof cell === 'number') return cell;
      // Handle strings with commas or quotes
      cell = String(cell).replace(/"/g, '""');
      if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
        cell = `"${cell}"`;
      }
      return cell;
    }).join(',');
  });
  
  return [csvHeader, ...csvRows].join('\n');
};

/**
 * Trigger download of CSV file
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export Button Component - Reusable across all dashboards
 */
export const ExportButton = ({ 
  brandId, 
  exportType, 
  dashboardType,
  label = "Download Full Report",
  filename,
  className = ""
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleExport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let endpoint = '';
      
      // Determine endpoint based on export type
      if (dashboardType) {
        endpoint = `${API_URL}/api/reports/export/dashboard/${dashboardType}?brand_id=${brandId}&limit=50000`;
      } else {
        switch(exportType) {
          case 'customers':
            endpoint = `${API_URL}/api/reports/export/customers?brand_id=${brandId}&limit=50000`;
            break;
          case 'transactions':
            endpoint = `${API_URL}/api/reports/export/transactions?brand_id=${brandId}&limit=50000`;
            break;
          case 'stores':
            endpoint = `${API_URL}/api/reports/export/stores?brand_id=${brandId}&limit=5000`;
            break;
          default:
            throw new Error(`Unknown export type: ${exportType}`);
        }
      }
      
      const response = await fetch(endpoint, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }
      
      // Convert to CSV and download
      let csvData = '';
      let csvFilename = filename || `${exportType || dashboardType}_export_${new Date().toISOString().split('T')[0]}.csv`;
      
      if (result.data && Array.isArray(result.data)) {
        csvData = jsonToCSV(result.data, result.columns);
      } else if (result.data && typeof result.data === 'object') {
        // Handle nested dashboard data
        if (result.data.monthly_trends) {
          csvData = jsonToCSV(result.data.monthly_trends);
          csvFilename = `monthly_trends_${csvFilename}`;
        } else if (result.data.summary) {
          // Convert summary object to array format
          const summaryData = [result.data.summary];
          csvData = jsonToCSV(summaryData);
        }
      }
      
      if (csvData) {
        downloadCSV(csvData, csvFilename);
      } else {
        throw new Error('No data to export');
      }
      
    } catch (err) {
      console.error('Export error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        onClick={handleExport}
        disabled={loading}
        className={`flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 
                   text-white rounded-lg transition-colors disabled:opacity-50 
                   disabled:cursor-not-allowed ${className}`}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        {loading ? 'Exporting...' : label}
      </button>
      {error && (
        <span className="text-red-400 text-sm">{error}</span>
      )}
    </div>
  );
};

/**
 * Quick Export Dropdown - Multiple export options
 */
export const ExportDropdown = ({ brandId, options = ['customers', 'transactions', 'stores'] }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 
                   text-white rounded-lg transition-colors"
      >
        <FileSpreadsheet className="w-4 h-4" />
        Export Data
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-xl z-50 
                       border border-gray-700 overflow-hidden">
          {options.includes('customers') && (
            <ExportButton 
              brandId={brandId} 
              exportType="customers"
              label="Export Customers"
              className="w-full rounded-none border-b border-gray-700 bg-gray-800 hover:bg-gray-700"
            />
          )}
          {options.includes('transactions') && (
            <ExportButton 
              brandId={brandId} 
              exportType="transactions"
              label="Export Transactions"
              className="w-full rounded-none border-b border-gray-700 bg-gray-800 hover:bg-gray-700"
            />
          )}
          {options.includes('stores') && (
            <ExportButton 
              brandId={brandId} 
              exportType="stores"
              label="Export Stores"
              className="w-full rounded-none bg-gray-800 hover:bg-gray-700"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ExportButton;
